import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
  Grid
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertPhoto as ImageIcon,
  InsertLink as LinkIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import uploadFile from "../../Utils/uploadFile";

export interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  url?: string;
  type: "image";
  name: string;
  size: number;
  uploading?: boolean;
  error?: string;
}

const HiddenFileInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  width: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  border: 0,
  padding: 0,
  margin: 0
});

interface FileUploadPreviewProps {
  uploadedFiles: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  businessId?: number;
  onImageInsert?: (url: string) => void;
  maxFiles?: number;
  maxFileSizeMB?: number;
}

const FileUploadPreview: React.FC<FileUploadPreviewProps> = ({
  uploadedFiles,
  onFilesChange,
  businessId,
  onImageInsert,
  maxFiles = 10,
  maxFileSizeMB = 10
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const validateFile = (file: File): string | null => {
    const maxSize = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      return `File size must be less than ${maxFileSizeMB}MB`;
    }

    const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
    const mimeType = (file.type || "").toLowerCase();

    if (!validImageTypes.includes(mimeType)) {
      return "Only PNG or JPG image files are allowed";
    }

    return null;
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadError(null);

    if (uploadedFiles.length + files.length > maxFiles) {
      setUploadError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const newFiles: UploadedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validationError = validateFile(file);

      if (validationError) {
        setUploadError(validationError);
        continue;
      }

      const fileType: "image" = "image";
      const preview = URL.createObjectURL(file);

      const uploadedFile: UploadedFile = {
        id: `${Date.now()}-${i}`,
        file,
        preview,
        type: fileType,
        name: file.name,
        size: file.size,
        uploading: true
      };

      newFiles.push(uploadedFile);
    }

    const updatedFiles = [...uploadedFiles, ...newFiles];
    onFilesChange(updatedFiles);

    // Upload files to S3 if businessId is provided
    if (businessId) {
      for (const uploadedFile of newFiles) {
        await uploadToS3(uploadedFile, updatedFiles);
      }
    } else {
      // If no businessId, just mark as ready (for preview only)
      const readyFiles = newFiles.map((f) => ({ ...f, uploading: false }));
      onFilesChange([...uploadedFiles, ...readyFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadToS3 = async (
    uploadedFile: UploadedFile,
    allFiles: UploadedFile[]
  ) => {
    try {
      const bucketName =
        process.env.REACT_APP_EMAIL_MANAGEMENT_BUCKET_NAME || "";
      console.log("S3 Bucket Name:", bucketName);

      if (!bucketName) {
        throw new Error(
          "Amazon S3 bucket is not configured. Please check REACT_APP_EMAIL_MANAGEMENT_BUCKET_NAME environment variable."
        );
      }
      const s3BaseUrl = process.env.REACT_APP_S3_BASE_URL || "";

      const paddedBusinessId =
        businessId?.toString()?.padStart(8, "0") || "00000000";
      const fileExtension = uploadedFile.file.name.split(".").pop();
      const key = `merchant/${paddedBusinessId}/emailcampaign_${Date.now()}_${
        uploadedFile.id
      }.${fileExtension}`;

      const uploadedUrl = await uploadFile(
        uploadedFile.file,
        bucketName,
        key,
        s3BaseUrl
      );

      if (uploadedUrl) {
        const updatedFiles = allFiles.map((f) =>
          f.id === uploadedFile.id
            ? { ...f, url: uploadedUrl, uploading: false }
            : f
        );
        onFilesChange(updatedFiles);
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      const updatedFiles = allFiles.map((f) =>
        f.id === uploadedFile.id
          ? { ...f, uploading: false, error: errorMessage }
          : f
      );
      onFilesChange(updatedFiles);
      setUploadError(`Failed to upload ${uploadedFile.name}: ${errorMessage}`);
    }
  };

  const handleRemoveFile = (id: string) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === id);
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    const updatedFiles = uploadedFiles.filter((f) => f.id !== id);
    onFilesChange(updatedFiles);
  };

  const handleInsertImage = (file: UploadedFile) => {
    if (onImageInsert) {
      const urlToInsert = file.url || file.preview;
      if (urlToInsert) {
        onImageInsert(urlToInsert);
      }
    }
  };

  const handleCopyLink = (file: UploadedFile) => {
    const urlToCopy = file.url || file.preview;
    if (urlToCopy) {
      navigator.clipboard.writeText(urlToCopy);
      // You could add a toast notification here
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={uploadedFiles.length >= maxFiles}
          sx={{
            borderColor: "#192F67",
            color: "#192F67",
            "&:hover": {
              borderColor: "#192F67",
              backgroundColor: "rgba(25, 47, 103, 0.04)"
            }
          }}
        >
          Upload Images
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            multiple
            onChange={handleFileSelect}
            aria-label="Upload images"
            title="Upload images"
          />
        </Button>
        <Typography variant="body2" color="text.secondary">
          {uploadedFiles.length} / {maxFiles} files uploaded
        </Typography>
      </Box>

      {uploadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setUploadError(null)}
        >
          {uploadError}
        </Alert>
      )}

      {uploadedFiles.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {uploadedFiles.map((file) => (
            <Grid item xs={12} sm={6} md={4} key={file.id}>
              <Card
                sx={{
                  position: "relative",
                  border: file.error
                    ? "2px solid #d32f2f"
                    : "1px solid #e0e0e0",
                  "&:hover": {
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {file.uploading ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 150,
                        gap: 1
                      }}
                    >
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        Uploading...
                      </Typography>
                    </Box>
                  ) : file.error ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 150,
                        gap: 1
                      }}
                    >
                      <Alert severity="error" sx={{ width: "100%" }}>
                        {file.error}
                      </Alert>
                    </Box>
                  ) : (
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={file.url || file.preview}
                        alt={file.name}
                        sx={{
                          width: "100%",
                          height: 150,
                          objectFit: "cover",
                          borderRadius: "4px",
                          display: "block"
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          display: "flex",
                          gap: 0.5,
                          backgroundColor: "rgba(0,0,0,0.5)",
                          borderRadius: 1
                        }}
                      >
                        {onImageInsert && (
                          <IconButton
                            size="small"
                            onClick={() => handleInsertImage(file)}
                            sx={{ color: "white", p: 0.5 }}
                            title="Insert into email"
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(file)}
                          sx={{ color: "white", p: 0.5 }}
                          title="Copy link"
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(file.id)}
                          sx={{ color: "white", p: 0.5 }}
                          title="Remove"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {uploadedFiles.length === 0 && (
        <Card
          sx={{
            border: "2px dashed #e0e0e0",
            backgroundColor: "#fafafa",
            textAlign: "center",
            py: 4
          }}
        >
          <CardContent>
            <CloudUploadIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No images uploaded yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload PNG or JPG images to use in your email campaign
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FileUploadPreview;
